import React, {
    useState,
    useCallback,
    useMemo,
    useRef,
    forwardRef,
    useImperativeHandle,
} from 'react';

import { Col, Row } from 'antd';
import PropTypes from 'prop-types';

import { extractRequestError } from '~/helpers/error-helper';
import { wrapForm } from '~/helpers/form-helper';
import { areObjectsEqual } from '~/helpers/validations';
import useAutoSubmitFilters from '~/hooks/use-auto-submit-filters';
import useDidMount from '~/hooks/use-did-mount';
import { DEFAULT_TABLE_PAGINATION } from '~/values/enums';

import Alert from '../Alert';
import FilterContainer from '../FilterContainer';
import Form from '../Form';
import Table from '../Table';

const DEFAULT_DATA_CONTENT = {
    resultList: [],
    pageSize: DEFAULT_TABLE_PAGINATION.limit,
    pageIndex: DEFAULT_TABLE_PAGINATION.page,
    total: DEFAULT_TABLE_PAGINATION.total,
    numberOfElements: DEFAULT_TABLE_PAGINATION.count,
};

const getOrderParam = sorter => {
    if (sorter.column && sorter.order) {
        return {
            order: sorter.order,
            field: sorter.column.dataSorter,
        };
    }

    return null;
};

const getColumnWithOrders = (column, orders) => {
    if (!column.props.dataSorter) return column;

    const sortOrder = orders?.field === column.props.dataSorter ? orders.order : false;
    return React.cloneElement(column, {
        sortOrder,
        sorter: true,
    });
};

const SimpleListContent = forwardRef(({
    handleSubmit, form,
    children, defaultFilters,
    tableRowKey, filterInputs,
    requestDataSource, loading,
    defaultOrders, tableScroll,
    basicFilter, tableRowSelection,
    ...otherProps
}, $ref) => {
    const [dataSource, setDataSource] = useState([]);
    const [requesting, setRequesting] = useState(false);
    const [requestParams, setRequestParams] = useState({});
    const [tablePagination, setTablePagination] = useState(DEFAULT_TABLE_PAGINATION);

    /**
     * Identifica a busca mais recente para ignorar respostas atrasadas de buscas antigas.
     */
    const requestIdRef = useRef(0);

    const { error, submitError } = form.getState();

    const filteredChildren = useMemo(() => {
        const { orders } = requestParams;

        return React.Children.toArray(children)
            .filter(Boolean)
            .map(column => getColumnWithOrders(column, orders));
    }, [children, requestParams]);

    const sendDataSourceRequest = useCallback(async (request, pagination) => {
        requestIdRef.current += 1;
        const requestId = requestIdRef.current;
        const isLastRequest = () => requestIdRef.current === requestId;

        try {
            setRequesting(true);
            const { filters = {}, orders = {} } = request;
            const { page, limit } = pagination;

            let data;
            try {
                const response = await requestDataSource(
                    filters,
                    orders,
                    page,
                    limit,
                );

                if (!response) return null;

                data = response.data;
            } catch (err) {
                if (!err.response?.status === 404) throw err;

                data = DEFAULT_DATA_CONTENT;
            }

            /*
             * Descarta a resposta de uma busca antiga que chegou depois de uma mais recente,
             * senão a listagem voltaria para o filtro anterior ao que o usuário pediu.
             */
            if (!isLastRequest()) return null;

            const {
                pageIndex,
                resultList, total, pageSize,
            } = data;

            setTablePagination({
                limit: pageSize,
                page: pageIndex,
                total,
                count: resultList?.length || 0,
            });
            setDataSource(resultList);
        } catch (err) {
            if (!isLastRequest()) return null;
            return await extractRequestError(err);
        } finally {
            if (isLastRequest()) {
                setRequesting(false);
            }
        }

        return null;
    }, [requestDataSource]);

    const onTableChange = useCallback(async (changedPagination, filters, sorter) => {
        const pagination = {
            ...tablePagination,
            page: changedPagination.current,
            limit: changedPagination.pageSize,
        };

        const params = {
            ...requestParams,
            orders: getOrderParam(sorter),
        };

        setTablePagination(pagination);
        setRequestParams(params);

        const response = await sendDataSourceRequest(params, pagination);
        return response;
    }, [tablePagination, requestParams, sendDataSourceRequest]);

    useDidMount(() => {
        setTimeout(() => {
            form.submit();
        }, 0);
    });

    const hasChangedFilters = useMemo(() => {
        return !areObjectsEqual(requestParams.filters, defaultFilters);
    }, [requestParams.filters, defaultFilters]);

    const onFilterSubmit = useCallback(async values => {
        const params = {
            ...requestParams,
            filters: values,
        };

        const pagination = {
            ...tablePagination,
            page: 1,
            limit: tablePagination.limit,
        };

        setRequestParams(params);
        setTablePagination(pagination);

        const response = await sendDataSourceRequest(params, pagination);
        return response;
    }, [requestParams, tablePagination, sendDataSourceRequest]);

    useAutoSubmitFilters(form, requestParams.filters);

    const handleClearFilters = useCallback(async () => {
        const params = {
            filters: defaultFilters,
            orders: defaultOrders,
        };

        setTablePagination(DEFAULT_TABLE_PAGINATION);
        setRequestParams(params);
        form.initialize(defaultFilters || {});

        const response = await sendDataSourceRequest(params, DEFAULT_TABLE_PAGINATION);
        return response;
    }, [defaultFilters, defaultOrders, sendDataSourceRequest, form]);

    useImperativeHandle(
        $ref,
        () => ({
            refresh: () => sendDataSourceRequest(requestParams, tablePagination),
            refilter: filters => {
                const newFilters = {
                    ...defaultFilters,
                    ...filters,
                };
                form.initialize(newFilters);
                return form.submit();
            },
            getFilters: () => requestParams.filters,
            getOrders: () => requestParams.orders,
            submit: () => form.submit(),
        }),
        [defaultFilters, form, requestParams, sendDataSourceRequest, tablePagination],
    );

    const renderFilters = () => {
        if (filterInputs) {
            const filteredInputs = filterInputs.map(input => {
                return (
                    <Col {...input.column} key={input.name}>
                        {input.component}
                    </Col>
                );
            });

            const showClearButton = hasChangedFilters && filteredInputs.length;

            return (
                <FilterContainer
                    loading={requesting}
                    onClearClick={showClearButton ? handleClearFilters : null}
                >
                    <Row gutter={24} align="middle">
                        {filteredInputs}
                    </Row>
                </FilterContainer>
            );
        }

        if (basicFilter) {
            return (
                <FilterContainer
                    loading={requesting}
                    onClearClick={hasChangedFilters ? handleClearFilters : null}
                >
                    {basicFilter}
                </FilterContainer>
            );
        }

        return null;
    };

    return (
        <div>
            <Alert type="error" message={error || submitError} />
            <Form onSubmit={handleSubmit(onFilterSubmit)}>
                {renderFilters()}
            </Form>
            <Table
                {...otherProps}
                rowKey={tableRowKey}
                scroll={tableScroll}
                dataSource={dataSource}
                onChange={onTableChange}
                page={tablePagination.page}
                limit={tablePagination.limit}
                total={tablePagination.total}
                loading={requesting || loading}
                rowSelection={tableRowSelection}
            >
                {filteredChildren}
            </Table>
        </div>
    );
});

SimpleListContent.propTypes = {
    loading: PropTypes.bool,
    requestDataSource: PropTypes.func.isRequired,
    defaultFilters: PropTypes.object,
    defaultOrders: PropTypes.shape({
        field: PropTypes.string.isRequired,
        order: PropTypes.oneOf(['descend', 'ascend']).isRequired,
    }),
    tableRowKey: PropTypes.func,
    tableScroll: PropTypes.object,
};

SimpleListContent.defaultProps = {
    loading: false,
    defaultFilters: undefined,
    filterInputs: undefined,
    defaultOrders: undefined,
    tableRowKey: item => item.id,
    tableScroll: {
        x: 1024,
    },
};

export default wrapForm(SimpleListContent);
